import React, { useEffect } from 'react';
import { toast } from 'react-toastify';
import { useModal } from './ModalProvider';
import { storagePwaShownSession, storageNotifLastShown } from '../utils/consts';
import {
  isIos,
  isAndroid,
  isStandalonePWA,
  supportsInstallPrompt,
} from '../utils/platform';
import {
  subscribe,
  getDeferredPrompt,
  promptInstall,
} from '../utils/installPrompt';
import {
  getNotificationPermission,
  enableNotifications,
} from '../utils/notifications';
import './PwaToast.scss';

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

// Returns true if the weekly throttle for the given storage key has elapsed.
const shouldShowWeekly = (key) => {
  const last = window.localStorage.getItem(key);
  return !last || Date.now() - new Date(last).getTime() >= WEEK_MS;
};

const markShown = (key) =>
  window.localStorage.setItem(key, new Date().toISOString());

// The install toast shows once per tab session (sessionStorage), so we keep
// nudging users toward the installed PWA on every fresh visit.
const shownThisSession = () =>
  window.sessionStorage.getItem(storagePwaShownSession) === '1';

const markSessionShown = () =>
  window.sessionStorage.setItem(storagePwaShownSession, '1');

export default function PwaToast() {
  const { openModal } = useModal();

  // iOS install instruction (only when not yet installed to Home Screen).
  useEffect(() => {
    const handleOpenInstruction = () => {
      openModal({
        header: 'How to install a PWA on an iPhone (iOS)',
        body: (
          <ol className='pwa-install-instruction'>
            <p className='font-16'>
              To install a Progressive Web App (PWA) on an iPhone, please follow
              these steps using the Safari browser:
            </p>
            <li>
              Open the website in <span className='font-medium'>Safari</span>{' '}
              (other browsers like Chrome or Firefox on iOS do not support PWA
              installation)
            </li>
            <li>Wait for the page to fully load.</li>
            <li>
              Tap the <span className='font-medium'>Share</span> icon (the
              square with an upward arrow, located at the bottom of the screen).
            </li>
            <li>
              Scroll down in the share menu and tap{' '}
              <span className='font-medium'>Add</span> to Home Screen.
            </li>
            <li>
              You will see a preview screen:
              <ul>
                <li>You can edit the name if desired.</li>
                <li>
                  Then tap <span className='font-medium'>Add</span> in the
                  upper-right corner.
                </li>
              </ul>
            </li>
            <li>
              The app will now appear on your home screen as an icon. You can
              open it like any native app.
            </li>
          </ol>
        ),
        buttons: [{ text: 'Ok' }],
      });
    };

    if (!isIos() || isStandalonePWA()) return;
    if (shownThisSession()) return;

    toast.info(
      <div className='toast' onClick={handleOpenInstruction}>
        <p>
          Install the app: In Safari, tap Share (↑), select “Add to Home
          Screen,” then tap “Add”
        </p>
        <p className='font-medium'>Click for instructions</p>
      </div>,
      {
        position: 'bottom-center',
        autoClose: false,
        closeOnClick: true,
        draggable: true,
      },
    );

    markSessionShown();
  }, [openModal]);

  // Android / Chromium install prompt. We capture `beforeinstallprompt`
  // (installPrompt.js) and drive the native install dialog ourselves, since
  // Chrome's automatic banner is throttled and unreliable. If the event never
  // arrives (Samsung Internet, Firefox, …) we fall back to a how-to toast.
  useEffect(() => {
    if (!isAndroid() || isStandalonePWA()) return;
    if (shownThisSession()) return;

    // Both toasts share one id so a late native prompt can upgrade the
    // already-shown instruction toast in place instead of being blocked.
    const toastId = 'pwa-install';
    let nativeShown = false;
    let instructionShown = false;

    const toastOptions = {
      toastId,
      position: 'bottom-center',
      autoClose: false,
      closeOnClick: true,
      draggable: true,
    };

    const handleInstall = async () => {
      try {
        await promptInstall();
      } catch (error) {
        toast.error(error?.message ?? 'Failed to open the install dialog');
      }
    };

    const handleOpenInstruction = () => {
      openModal({
        header: 'How to install the app on Android',
        body: (
          <ol className='pwa-install-instruction'>
            <p className='font-16'>
              To install the app on Android, please follow these steps:
            </p>
            <li>
              Open the browser menu (the <span className='font-medium'>⋮</span>{' '}
              icon, usually in the top-right corner).
            </li>
            <li>
              Tap <span className='font-medium'>Install app</span> (or{' '}
              <span className='font-medium'>Add to Home screen</span>).
            </li>
            <li>
              Confirm by tapping <span className='font-medium'>Install</span>.
            </li>
            <li>
              The app will appear on your home screen. You can open it like any
              native app.
            </li>
          </ol>
        ),
        buttons: [{ text: 'Ok' }],
      });
    };

    // Native one-tap install when Chrome has offered the prompt. If the
    // fallback instruction toast is already up, upgrade it in place.
    const showNative = () => {
      if (nativeShown) return;
      nativeShown = true;
      const content = (
        <div className='toast' onClick={handleInstall}>
          <p>Install Muva for quick access and notifications.</p>
          <p className='font-medium'>Tap to install</p>
        </div>
      );
      if (instructionShown) toast.update(toastId, { render: content });
      else toast.info(content, toastOptions);
      markSessionShown();
    };

    // Fallback how-to toast for browsers that don't fire beforeinstallprompt.
    const showInstruction = () => {
      if (instructionShown || nativeShown) return;
      instructionShown = true;
      toast.info(
        <div className='toast' onClick={handleOpenInstruction}>
          <p>
            Install the app: open the browser menu (⋮), then tap “Install app.”
          </p>
          <p className='font-medium'>Click for instructions</p>
        </div>,
        toastOptions,
      );
      markSessionShown();
    };

    const unsubscribe = subscribe(() => {
      if (getDeferredPrompt()) showNative();
    });

    // Manual instructions are only for browsers that never fire
    // beforeinstallprompt (e.g. Firefox Android). Chromium browsers either
    // fire the event (installable) or stay silent (already installed) — in
    // both cases we must not nag with a how-to toast.
    const timer = setTimeout(() => {
      if (getDeferredPrompt() || nativeShown) return;
      if (supportsInstallPrompt()) return;
      showInstruction();
    }, 3000);

    return () => {
      unsubscribe();
      clearTimeout(timer);
    };
  }, [openModal]);

  // Enable-notifications prompt. Only inside an installed PWA — in a regular
  // browser users already see in-app toasts for new messages. On iOS the
  // permission request needs a user gesture, so we show a tap-to-enable toast;
  // elsewhere we request right away and let the browser show its native dialog.
  useEffect(() => {
    if (getNotificationPermission() !== 'default') return;
    if (!isStandalonePWA()) return;
    if (!shouldShowWeekly(storageNotifLastShown)) return;

    if (isIos()) {
      const handleEnable = async () => {
        try {
          await enableNotifications();
          toast.success('Notifications enabled');
        } catch (error) {
          toast.error(error?.message ?? 'Failed to enable notifications');
        }
      };

      toast.info(
        <div className='toast' onClick={handleEnable}>
          <p>Get notified about new messages.</p>
          <p className='font-medium'>Click to enable notifications</p>
        </div>,
        {
          position: 'bottom-center',
          autoClose: false,
          closeOnClick: true,
          draggable: true,
        },
      );
    } else {
      enableNotifications().catch(() => {});
    }

    markShown(storageNotifLastShown);
  }, []);

  // This component renders nothing; ToastContainer is placed in App
  return null;
}
